import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface FormData {
  name: string;
  company: string;
  email: string;
  phone: string;
  city: string;
  headcount: string;
  service: string;
  message: string;
}

const empty: FormData = {
  name: "",
  company: "",
  email: "",
  phone: "",
  city: "",
  headcount: "",
  service: "Workplace catering",
  message: "",
};

const WEB3FORMS_KEY = "5fad6c0e-f956-40cc-9a26-58bef6439ca4";

export function Contact() {
  const [form, setForm] = useState<FormData>(empty);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");

  function set(field: keyof FormData, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
    if (sendError) setSendError("");
  }

  function validate(): boolean {
    const e: Partial<Record<keyof FormData, string>> = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.company.trim()) e.company = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Invalid email";
    if (!form.phone.trim()) e.phone = "Required";
    else if (!/^\d{10}$/.test(form.phone.replace(/\s/g, ""))) e.phone = "Must be 10 digits";
    if (!form.city.trim()) e.city = "Required";
    if (!form.headcount.trim()) e.headcount = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    setSending(true);
    setSendError("");

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: `New inquiry from ${form.name} — ${form.company}`,
          from_name: form.name,
          name: form.name,
          company: form.company,
          email: form.email,
          phone: form.phone,
          city: form.city,
          headcount: form.headcount,
          service: form.service,
          message: form.message || "—",
        }),
      });

      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
      } else {
        setSendError(data.message || "Something went wrong. Please try again.");
      }
    } catch {
      setSendError("Network error. Please check your connection and try again.");
    } finally {
      setSending(false);
    }
  }

  return (
    <motion.section
      id="contact"
      className="relative py-20 sm:py-28 md:py-40"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      <div className="absolute top-0 left-0 right-0 hairline" />
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16">

        {/* Left info column */}
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-5 sm:mb-6">
            <span className="h-px w-12 bg-gold" />
            <span className="text-xs uppercase tracking-[0.4em] text-gold">Get in touch</span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-6xl leading-tight">
            Let's plan your{" "}
            <span className="gradient-gold-text italic">next meal.</span>
          </h2>
          <p className="mt-5 sm:mt-6 text-muted-foreground text-base sm:text-lg font-light">
            Tell us about your team or event — we'll design a tasting menu and proposal
            tailored to your workplace within 48 hours.
          </p>

          <div className="mt-8 sm:mt-10 space-y-5 sm:space-y-6">
            <div className="group flex items-baseline gap-4 sm:gap-6 border-b border-[var(--gold)]/15 pb-4 sm:pb-5">
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.25em] sm:tracking-[0.3em] text-muted-foreground w-24 sm:w-28 flex-none">
                Email
              </div>
              <a
                href="mailto:support@mononites.com"
                className="text-gold hover:text-gold/80 transition-colors text-base sm:text-lg font-light underline underline-offset-4 decoration-gold/40 hover:decoration-gold"
              >
                support@mononites.com
                <span className="inline-block ml-2 text-xs no-underline opacity-60">↗ tap to email</span>
              </a>
            </div>
            <ContactRow label="Primary" value="+91 98849 22299" href="tel:+919884922299" />
            <ContactRow label="Secondary" value="+91 99620 76757" href="tel:+919962076757" />
            <div className="group flex items-center gap-4 sm:gap-6 border-b border-[var(--gold)]/15 pb-4 sm:pb-5">
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.25em] sm:tracking-[0.3em] text-muted-foreground w-24 sm:w-28 flex-none">
                Hours
              </div>
              <div className="flex items-center gap-2.5 text-foreground text-base sm:text-lg font-light">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                </span>
                <span className="text-emerald-400">Open 24 Hours</span>
              </div>
            </div>
            <ContactRow label="Service area" value="Chennai" />
          </div>
        </div>

        {/* Right form / success column */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-7 glass-card rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 shadow-elevated overflow-hidden"
        >
          <AnimatePresence mode="wait">
            {submitted ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="flex flex-col items-center justify-center text-center py-10 sm:py-16"
              >
                <div className="h-20 w-20 rounded-full bg-emerald-500/15 grid place-items-center mb-6">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-400">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h3 className="font-display text-2xl sm:text-3xl text-gold mb-3">Thank you!</h3>
                <p className="text-muted-foreground text-sm sm:text-base font-light max-w-sm">
                  Your inquiry has been received. Our team will get back to you within 48 hours with a tailored proposal.
                </p>
                <button
                  onClick={() => { setForm(empty); setErrors({}); setSubmitted(false); }}
                  className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[var(--gold)]/40 text-gold text-xs font-medium uppercase tracking-[0.12em] hover:border-gold transition-all"
                >
                  Submit another inquiry
                </button>
              </motion.div>
            ) : (
              <motion.div
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <h3 className="font-display text-xl sm:text-2xl mb-6 sm:mb-8">Send us an inquiry</h3>
                <form onSubmit={handleSubmit} noValidate>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                    <Field label="Full name" value={form.name} error={errors.name} onChange={(v) => set("name", v)} />
                    <Field label="Company" value={form.company} error={errors.company} onChange={(v) => set("company", v)} />
                    <Field label="Work email" value={form.email} type="email" error={errors.email} onChange={(v) => set("email", v)} />
                    <Field label="Phone" value={form.phone} type="tel" error={errors.phone} placeholder="10 digit number" onChange={(v) => set("phone", v)} />
                    <Field label="City" value={form.city} error={errors.city} onChange={(v) => set("city", v)} />
                    <Field label="Approx. headcount" value={form.headcount} error={errors.headcount} onChange={(v) => set("headcount", v)} />
                  </div>
                  <div className="mt-4 sm:mt-5">
                    <label className="block text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">
                      Service of interest
                    </label>
                    <select
                      value={form.service}
                      onChange={(e) => set("service", e.target.value)}
                      className="w-full bg-transparent border border-[var(--gold)]/25 rounded-xl px-4 py-3 text-foreground focus:outline-none focus:border-gold transition text-sm"
                    >
                      <option className="bg-[var(--navy-deep)]">Workplace catering</option>
                      <option className="bg-[var(--navy-deep)]">Cafeteria management</option>
                      <option className="bg-[var(--navy-deep)]">Event dining</option>
                      <option className="bg-[var(--navy-deep)]">Wellness meal program</option>
                    </select>
                  </div>
                  <div className="mt-4 sm:mt-5">
                    <label className="block text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">
                      Tell us more
                    </label>
                    <textarea
                      value={form.message}
                      onChange={(e) => set("message", e.target.value)}
                      rows={4}
                      className="w-full bg-transparent border border-[var(--gold)]/25 rounded-xl px-4 py-3 text-foreground focus:outline-none focus:border-gold transition resize-none text-sm"
                      placeholder="Event details, timeline, dietary preferences…"
                    />
                  </div>
                  {sendError && (
                    <p className="mt-4 text-sm text-red-400">{sendError}</p>
                  )}
                  <button
                    type="submit"
                    disabled={sending}
                    className="mt-6 sm:mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 sm:px-10 py-3.5 sm:py-4 rounded-full bg-gold text-[var(--gold-foreground)] font-medium uppercase tracking-[0.15em] text-sm hover:shadow-gold hover:-translate-y-0.5 active:translate-y-0 transition-all disabled:opacity-60 disabled:pointer-events-none"
                  >
                    {sending ? "Sending…" : "Send inquiry →"}
                  </button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </motion.section>
  );
}

function Field({
  label,
  value,
  type = "text",
  error,
  placeholder,
  onChange,
}: {
  label: string;
  value: string;
  type?: string;
  error?: string;
  placeholder?: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">
        {label}
      </label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full bg-transparent border rounded-xl px-4 py-3 text-foreground text-sm focus:outline-none transition ${
          error ? "border-red-500 focus:border-red-400" : "border-[var(--gold)]/25 focus:border-gold"
        }`}
      />
      {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
    </div>
  );
}

function ContactRow({
  label,
  value,
  href,
}: {
  label: string;
  value: string;
  href?: string;
}) {
  const inner = (
    <div className="group flex items-baseline gap-4 sm:gap-6 border-b border-[var(--gold)]/15 pb-4 sm:pb-5">
      <div className="text-[10px] sm:text-xs uppercase tracking-[0.25em] sm:tracking-[0.3em] text-muted-foreground w-24 sm:w-28 flex-none">
        {label}
      </div>
      <div className="text-foreground group-hover:text-gold transition-colors text-base sm:text-lg font-light">
        {value}
      </div>
    </div>
  );
  return href ? <a href={href}>{inner}</a> : inner;
}
